import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Layout from '../components/Layout';
import { useAuth } from '../context/AuthContext';
import { getTickets, getTicket, updateTicket, replyTicket } from '../utils/api';
import { useIsMobile } from '../hooks/useIsMobile';
import { formatDate, formatDistanceToNow } from '../utils/helpers';
import toast from 'react-hot-toast';
import {
  LifeBuoy, Send, RefreshCw, Filter, Tag, Clock, ChevronRight,
  CheckCircle2, ArrowLeft, Building2, User, ChevronDown,
  AlertCircle, Circle, X,
} from 'lucide-react';

const CATEGORIES = {
  billing: 'Billing', bug: 'Bug / Error', account: 'Account',
  feature: 'Feature Request', general: 'General',
};
const STATUS_OPTS = ['open','in_progress','waiting','resolved','closed'];
const PRIORITY_OPTS = ['low','normal','high','urgent'];

function StatusBadge({ status, small }) {
  const map = {
    open:        { color: 'var(--blue)',    bg: 'var(--blue-dim)',    label: 'Open'           },
    in_progress: { color: 'var(--amber)',   bg: 'var(--amber-dim)',   label: 'In Progress'    },
    waiting:     { color: 'var(--purple)',  bg: 'var(--purple-dim)',  label: 'Waiting'        },
    resolved:    { color: 'var(--green)',   bg: 'var(--green-dim)',   label: 'Resolved'       },
    closed:      { color: 'var(--text-muted)', bg: 'rgba(0,0,0,0.06)', label: 'Closed'       },
  };
  const s = map[status] || map.open;
  return (
    <span style={{
      fontSize: small ? '0.6rem' : '0.65rem', fontWeight: 700,
      color: s.color, background: s.bg,
      padding: small ? '1px 6px' : '2px 8px', borderRadius: 4,
      textTransform: 'uppercase', letterSpacing: '0.06em', whiteSpace: 'nowrap',
    }}>
      {s.label}
    </span>
  );
}

function PriorityBadge({ priority }) {
  const map = {
    low:    { color: 'var(--text-muted)', label: 'Low' },
    normal: { color: 'var(--blue)',       label: 'Normal' },
    high:   { color: 'var(--amber)',      label: 'High' },
    urgent: { color: 'var(--red)',        label: 'Urgent' },
  };
  const p = map[priority] || map.normal;
  return (
    <span style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: '0.68rem', color: p.color, fontWeight: 600 }}>
      <span style={{ width: 6, height: 6, borderRadius: '50%', background: p.color, display: 'inline-block' }} />
      {p.label}
    </span>
  );
}

function TicketDetail({ ticketId, onBack, adminId, adminName, onUpdated }) {
  const [ticket,  setTicket]  = useState(null);
  const [loading, setLoading] = useState(true);
  const [reply,   setReply]   = useState('');
  const [sending, setSending] = useState(false);
  const [updating,setUpdating]= useState(false);
  const scrollRef = useRef(null);

  const loadTicket = useCallback(async () => {
    try {
      const data = await getTicket(ticketId);
      setTicket(data);
    } catch { toast.error('Failed to load ticket'); }
    finally { setLoading(false); }
  }, [ticketId]);

  useEffect(() => { loadTicket(); }, [loadTicket]);
  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [ticket?.messages?.length]);

  const handleReply = async () => {
    if (!reply.trim() || sending) return;
    setSending(true);
    try {
      await replyTicket(ticketId, reply.trim());
      setReply('');
      await loadTicket();
      onUpdated?.();
      toast.success('Reply sent');
    } catch (err) { toast.error(err.message || 'Failed'); }
    finally { setSending(false); }
  };

  const handleUpdate = async (field, value) => {
    setUpdating(true);
    try {
      await updateTicket(ticketId, { [field]: value });
      await loadTicket();
      onUpdated?.();
      toast.success('Ticket updated');
    } catch (err) { toast.error(err.message || 'Failed'); }
    finally { setUpdating(false); }
  };

  if (loading) return <div className="loading-center"><div className="spinner" /></div>;
  if (!ticket) return null;

  const isClosed = ['resolved','closed'].includes(ticket.status);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Ticket header */}
      <div style={{ marginBottom: 16, flexShrink: 0 }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, marginBottom: 12 }}>
          <button className="btn btn-ghost btn-sm" onClick={onBack} style={{ flexShrink: 0 }}>
            <ArrowLeft size={13} /> All Tickets
          </button>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 700, fontSize: '0.95rem', marginBottom: 5 }}>{ticket.subject}</div>
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
              <StatusBadge status={ticket.status} />
              <PriorityBadge priority={ticket.priority} />
              <span style={{ fontFamily: 'var(--font-mono)', fontSize: '0.68rem', color: 'var(--text-muted)' }}>
                <Building2 size={10} style={{ marginRight: 3, verticalAlign: '-1px' }} />{ticket.org_name}
              </span>
              <span style={{ fontFamily: 'var(--font-mono)', fontSize: '0.68rem', color: 'var(--text-muted)' }}>
                <User size={10} style={{ marginRight: 3, verticalAlign: '-1px' }} />{ticket.submitter_name} ({ticket.submitter_email})
              </span>
            </div>
          </div>
        </div>

        {/* Controls */}
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <div>
            <select
              className="form-select"
              style={{ padding: '5px 10px', fontSize: '0.72rem' }}
              value={ticket.status}
              onChange={e => handleUpdate('status', e.target.value)}
              disabled={updating}
            >
              {STATUS_OPTS.map(s => <option key={s} value={s}>{s.replace('_',' ').replace(/\b\w/g,c=>c.toUpperCase())}</option>)}
            </select>
          </div>
          <div>
            <select
              className="form-select"
              style={{ padding: '5px 10px', fontSize: '0.72rem' }}
              value={ticket.priority}
              onChange={e => handleUpdate('priority', e.target.value)}
              disabled={updating}
            >
              {PRIORITY_OPTS.map(p => <option key={p} value={p}>{p.charAt(0).toUpperCase()+p.slice(1)}</option>)}
            </select>
          </div>
          <button
            className="btn btn-ghost btn-sm"
            onClick={() => handleUpdate('assigned_to', adminId)}
            disabled={updating || ticket.assigned_to === adminId}
            style={{ fontSize: '0.68rem' }}
          >
            {ticket.assigned_to === adminId ? ' Assigned to you' : 'Assign to me'}
          </button>
          <button
            className="btn btn-ghost btn-sm"
            onClick={() => window.open(`https://app.zeuscms.com`, '_blank')}
            style={{ fontSize: '0.68rem', marginLeft: 'auto' }}
          >
            <Building2 size={11} /> View Org
          </button>
        </div>
      </div>

      {/* Messages */}
      <div ref={scrollRef} className="card" style={{ flex: 1, overflow: 'auto', padding: 16, display: 'flex', flexDirection: 'column', gap: 12, marginBottom: 12 }}>
        {(ticket.messages || []).map(msg => {
          const isAdmin = msg.sender_type === 'admin';
          return (
            <div key={msg.id} style={{ display: 'flex', gap: 10, alignItems: 'flex-start', flexDirection: isAdmin ? 'row-reverse' : 'row' }}>
              <div style={{
                width: 30, height: 30, borderRadius: '50%', flexShrink: 0,
                background: isAdmin ? 'var(--gold-dim)' : 'var(--bg-hover)',
                border: `1px solid ${isAdmin ? 'var(--border-gold)' : 'var(--border)'}`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: '0.62rem', fontWeight: 700,
                color: isAdmin ? 'var(--gold)' : 'var(--text-secondary)',
              }}>
                {(msg.sender_name || (isAdmin ? 'A' : 'U'))[0].toUpperCase()}
              </div>
              <div style={{ maxWidth: '78%' }}>
                <div style={{
                  padding: '10px 14px', borderRadius: isAdmin ? '4px 12px 12px 12px' : '12px 4px 12px 12px',
                  background: isAdmin ? 'var(--gold-dim)' : 'var(--bg-hover)',
                  border: `1px solid ${isAdmin ? 'var(--border-gold)' : 'var(--border-dim)'}`,
                  fontSize: '0.82rem', lineHeight: 1.65, color: 'var(--text-primary)',
                }}>
                  {msg.body}
                </div>
                <div style={{ fontSize: '0.62rem', color: 'var(--text-muted)', marginTop: 3, textAlign: isAdmin ? 'right' : 'left' }}>
                  {msg.sender_name} · {formatDistanceToNow(msg.created_at)}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Reply box */}
      {!isClosed && (
        <div style={{ flexShrink: 0 }}>
          <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}>
            <textarea
              className="form-input"
              style={{ flex: 1, resize: 'none', fontFamily: 'var(--font-sans)', lineHeight: 1.6, minHeight: 80, fontSize: '0.85rem' }}
              placeholder="Type your reply to the user… (Cmd+Enter to send)"
              value={reply}
              onChange={e => setReply(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) handleReply(); }}
              autoFocus
            />
            <button
              className="btn btn-primary"
              onClick={handleReply}
              disabled={!reply.trim() || sending}
              style={{ flexShrink: 0, alignSelf: 'flex-end' }}
            >
              {sending ? <span className="spinner" style={{ width: 13, height: 13 }} /> : <Send size={13} />}
              Send Reply
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default function SupportPage() {
  const { user: admin }     = useAuth();
  const isMobile  = useIsMobile();
  const adminId   = admin?.id;
  const adminName = `${admin?.first_name || ''} ${admin?.last_name || ''}`.trim() || admin?.email;

  const [tickets,      setTickets]      = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [openCount,    setOpenCount]    = useState(0);
  const [activeId,     setActiveId]     = useState(null);
  const [statusFilter, setStatusFilter] = useState('');
  const [assignFilter, setAssignFilter] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (statusFilter) params.set('status', statusFilter);
      if (assignFilter) params.set('assigned', assignFilter);
      const data = await getTickets(Object.fromEntries(params));
      setTickets(data.tickets || []);
      setOpenCount(data.open_count || 0);
    } catch { toast.error('Failed to load support tickets'); }
    finally { setLoading(false); }
  }, [statusFilter, assignFilter]);

  useEffect(() => { load(); }, [load]);

  if (activeId) {
    return (
      <Layout title="Support" crumb="Ticket Detail">
        <div style={{ height: 'calc(100vh - 120px)', display: 'flex', flexDirection: 'column' }}>
          <TicketDetail
            ticketId={activeId}
            adminId={adminId}
            adminName={adminName}
            onBack={() => { setActiveId(null); load(); }}
            onUpdated={load}
          />
        </div>
      </Layout>
    );
  }

  const urgentCount = tickets.filter(t => t.priority === 'urgent' && !['resolved','closed'].includes(t.status)).length;

  return (
    <Layout title="Support" crumb={`${openCount} open`}>

      {/* Urgent alert */}
      {urgentCount > 0 && (
        <div style={{ background: 'var(--red-dim)', border: '1px solid rgba(239,68,68,0.25)', borderRadius: 'var(--r-lg)', padding: '10px 16px', marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8, fontSize: '0.8rem' }}>
          <AlertCircle size={14} style={{ color: 'var(--red)', flexShrink: 0 }} />
          <span><strong style={{ color: 'var(--red)' }}>{urgentCount} urgent ticket{urgentCount > 1 ? 's' : ''}</strong> need{urgentCount === 1 ? 's' : ''} immediate attention</span>
        </div>
      )}

      {/* Toolbar */}
      <div className="toolbar-inline" style={{ display: 'flex', gap: 8, marginBottom: 14, flexWrap: 'wrap', alignItems: 'center' }}>
        <select className="form-select" style={{ width: 'auto', padding: '7px 12px' }} value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
          <option value="">All Statuses</option>
          {STATUS_OPTS.map(s => <option key={s} value={s}>{s.replace('_',' ').replace(/\b\w/g,c=>c.toUpperCase())}</option>)}
        </select>
        <select className="form-select" style={{ width: 'auto', padding: '7px 12px' }} value={assignFilter} onChange={e => setAssignFilter(e.target.value)}>
          <option value="">All Assignments</option>
          <option value="me">Assigned to Me</option>
          <option value="none">Unassigned</option>
        </select>
        <div style={{ flex: 1 }} />
        <button className="btn btn-ghost btn-sm" onClick={load}><RefreshCw size={13} /> Refresh</button>
      </div>

      {/* Ticket table (desktop) / cards (mobile) */}
      {loading ? (
        <div className="loading-center"><div className="spinner" /> Loading tickets…</div>
      ) : !isMobile ? (
        <div className="card">
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Subject</th>
                  <th>Org</th>
                  <th>User</th>
                  <th>Category</th>
                  <th>Priority</th>
                  <th>Status</th>
                  <th>Last Activity</th>
                </tr>
              </thead>
              <tbody>
                {tickets.map(t => {
                  const needsAttention = t.last_reply_by === 'user' && !['resolved','closed'].includes(t.status);
                  return (
                    <tr
                      key={t.id}
                      style={{ cursor: 'pointer', fontWeight: needsAttention ? 600 : 400 }}
                      onClick={() => setActiveId(t.id)}
                    >
                      <td>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                          {needsAttention && (
                            <span style={{ width: 7, height: 7, borderRadius: '50%', background: 'var(--blue)', flexShrink: 0, boxShadow: '0 0 5px rgba(59,130,246,0.5)' }} />
                          )}
                          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 240 }}>{t.subject}</span>
                        </div>
                      </td>
                      <td>
                        <div style={{ fontSize: '0.78rem' }}>{t.org_name}</div>
                      </td>
                      <td>
                        <div style={{ fontSize: '0.75rem' }}>{t.submitter_name}</div>
                        <div className="mono-sm">{t.submitter_email}</div>
                      </td>
                      <td><span className="badge badge-dim">{CATEGORIES[t.category] || t.category}</span></td>
                      <td><PriorityBadge priority={t.priority} /></td>
                      <td><StatusBadge status={t.status} small /></td>
                      <td className="mono-sm">{formatDistanceToNow(t.last_reply_at || t.created_at)}</td>
                    </tr>
                  );
                })}
                {tickets.length === 0 && (
                  <tr><td colSpan={7}>
                    <div className="empty">
                      <LifeBuoy size={28} />
                      <h3>No tickets found</h3>
                      <p>Support requests from firms will appear here.</p>
                    </div>
                  </td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      ) : null}

          {/* Mobile ticket cards */}
          {isMobile && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {tickets.map(t => {
                const needsAttention = t.last_reply_by === 'user' && !['resolved','closed'].includes(t.status);
                return (
                  <div key={t.id} className="card" style={{ padding: '12px 14px', cursor: 'pointer', display: 'flex', gap: 10, alignItems: 'flex-start' }}
                    onClick={() => setActiveId(t.id)}>
                    {needsAttention && (
                      <span style={{ width: 7, height: 7, borderRadius: '50%', background: 'var(--blue)', flexShrink: 0, marginTop: 6, boxShadow: '0 0 5px rgba(59,130,246,0.5)' }} />
                    )}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4, flexWrap: 'wrap' }}>
                        <span style={{ fontWeight: 600, fontSize: '0.85rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '55vw' }}>{t.subject}</span>
                        <StatusBadge status={t.status} small />
                      </div>
                      <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', marginBottom: 2 }}>{t.org_name} · {t.submitter_name}</div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <PriorityBadge priority={t.priority} />
                        <span style={{ fontFamily: 'var(--font-mono)', fontSize: '0.62rem', color: 'var(--text-muted)' }}>{formatDistanceToNow(t.last_reply_at || t.created_at)}</span>
                      </div>
                    </div>
                    <ChevronRight size={14} style={{ color: 'var(--text-muted)', flexShrink: 0, marginTop: 4 }} />
                  </div>
                );
              })}
              {tickets.length === 0 && (
                <div className="empty"><LifeBuoy size={28} /><h3>No tickets found</h3><p>Support requests from firms will appear here.</p></div>
              )}
            </div>
          )}

    </Layout>
  );
}
